import { Box, Heading, Skeleton, VStack } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { ReportingAccessorEntityAttributesGQLType } from '../../../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from '~/packages/common-frontend/colors';
import { useGetSuccessFinancialCovenantsByKeyQuery } from '~/packages/covenants/requests/financialCovenants.gql.generated';
import {
  financialCovenantValueField,
  getThresholdType,
} from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantValue/thresholdValueUtils';
import { CovenantChartDetails } from '~/packages/portfolio/ui/covenants/CovenantChartDetails';
import {
  defaultFinancialCovenantValueType,
  expandCovenantsChartData,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';

type DigitalMonthlyReportFinancialCovenantOverviewCardProps = {
  creditFacilityId: string;
  creditAgreementFinancialCovenant:
    | CreditAgreement.DomainEntity.FinancialCovenantProperty
    | undefined;
  covenantKey: string;
  financialCovenantId: string;
};
const DigitalMonthlyReportFinancialCovenantOverviewCard = ({
  creditFacilityId,
  creditAgreementFinancialCovenant,
  covenantKey,
  financialCovenantId,
}: DigitalMonthlyReportFinancialCovenantOverviewCardProps) => {
  const thresholdType = useMemo(() => {
    return creditAgreementFinancialCovenant &&
      creditAgreementFinancialCovenant._config_
      ? getThresholdType(
          'financial',
          creditAgreementFinancialCovenant._config_.intermediateFields,
        )
      : null;
  }, [creditAgreementFinancialCovenant]);

  const financialCovenantValueFormula = useMemo(() => {
    return creditAgreementFinancialCovenant &&
      creditAgreementFinancialCovenant._config_
      ? creditAgreementFinancialCovenant._config_.intermediateFields.find(
          (intermediateField) =>
            intermediateField.field === financialCovenantValueField,
        )?.config.formula
      : null;
  }, [creditAgreementFinancialCovenant]);

  const {
    data: successFinancialCovenantsForKeyData,
    loading: isSuccessFinancialCovenantsForKeyLoading,
  } = useGetSuccessFinancialCovenantsByKeyQuery({
    variables: { creditFacilityId, covenantKey, mode: 'RESULT' },
  });

  const expandedFinancialCovenantDataPoint = useMemo(() => {
    if (!successFinancialCovenantsForKeyData) {
      return null;
    }

    const financialCovenantUsedForMonthlyReport = successFinancialCovenantsForKeyData.successFinCovsByKey.find(
      (successFinCov) => successFinCov.id === financialCovenantId,
    );

    if (!financialCovenantUsedForMonthlyReport) {
      return null;
    }

    return expandCovenantsChartData([financialCovenantUsedForMonthlyReport]);
  }, [successFinancialCovenantsForKeyData, financialCovenantId]);

  const finCovValueType =
    creditAgreementFinancialCovenant?.limit.valueType ??
    defaultFinancialCovenantValueType;

  if (
    !successFinancialCovenantsForKeyData ||
    isSuccessFinancialCovenantsForKeyLoading
  ) {
    return <Skeleton height="400px" />;
  }

  if (expandedFinancialCovenantDataPoint) {
    return (
      <Box>
        <CovenantChartDetails
          selectedDataPoint={expandedFinancialCovenantDataPoint[0]}
          thresholdType={thresholdType}
          covenantValueFormula={financialCovenantValueFormula}
          valueType={finCovValueType}
        />
      </Box>
    );
  }

  return null;
};

type DigitalMonthlyReportFinancialCovenantOverviewProps = {
  creditFacilityId: string;
  creditAgreementFinancialCovenants: CreditAgreement.DomainEntity.FinancialCovenantProperty[];
  financialCovenantAttributes: ReportingAccessorEntityAttributesGQLType[];
};
export const DigitalMonthlyReportFinancialCovenantOverview = ({
  creditFacilityId,
  creditAgreementFinancialCovenants,
  financialCovenantAttributes,
}: DigitalMonthlyReportFinancialCovenantOverviewProps) => {
  return (
    <VStack spacing="40px">
      {financialCovenantAttributes.map((financialCovenantAttribute) => {
        const {
          supplementalEntityKey: covenantKey,
          identifier,
        } = financialCovenantAttribute;

        const financialCovenantId = commonUtils.parseDbPgIdPointer(identifier);

        if (!covenantKey || !financialCovenantId) {
          return null;
        }

        const creditAgreementFinancialCovenant = creditAgreementFinancialCovenants.find(
          (financialCovenant) => financialCovenant.covenantKey === covenantKey,
        );

        return (
          <Box
            key={covenantKey}
            backgroundColor="white"
            borderRadius="8px"
            borderWidth="1px"
            borderColor={colors.gray[200]}
            padding="40px"
            w="100%"
          >
            <Heading as="h2" fontSize="24px" mb="32px">
              {commonUtils.toTitleCase(covenantKey)}
            </Heading>
            <DigitalMonthlyReportFinancialCovenantOverviewCard
              creditFacilityId={creditFacilityId}
              creditAgreementFinancialCovenant={
                creditAgreementFinancialCovenant
              }
              covenantKey={covenantKey}
              financialCovenantId={financialCovenantId}
            />
          </Box>
        );
      })}
    </VStack>
  );
};
